import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { leaveApi } from "../../services/leaveApi";
import { usePermissions } from "../../hooks/usePermissions";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { formatApiErrorDetail } from "../../lib/api";
import { useLocalization } from "../../hooks/useLocalization";

export default function LeavePoliciesPage() {
  const { t } = useLocalization();
  const { canViewLeave, canManageLeave } = usePermissions();
  const [policies, setPolicies] = useState([]);
  const [form, setForm] = useState({ key: "", title_en: "", title_ja: "", grant_type: "ANNUAL", annual_days: "", effective_from: "", source_document: "" });

  const load = () => leaveApi.listPolicies().then((r) => setPolicies(r.data)).catch(() => {});
  useEffect(() => { if (canViewLeave) load(); }, [canViewLeave]);

  if (!canViewLeave) return <p className="text-sm text-gray-500" data-testid="leave-policies-access-denied">{t("mgmtLeavePolicies.accessDenied")}</p>;

  const createPolicy = async () => {
    if (!form.key || !form.title_en || !form.annual_days || !form.effective_from) { toast.error(t("mgmtLeavePolicies.keyRequired")); return; }
    try {
      await leaveApi.createPolicy({ ...form, title_ja: form.title_ja || form.title_en, annual_days: Number(form.annual_days) });
      toast.success(t("mgmtLeavePolicies.policyCreated"));
      setForm({ key: "", title_en: "", title_ja: "", grant_type: "ANNUAL", annual_days: "", effective_from: "", source_document: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const approve = async (id) => {
    try { await leaveApi.approvePolicy(id); toast.success(t("mgmtLeavePolicies.policyApproved")); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="leave-policies-page">
      <PageHelpBanner pageKey="leave-policies" title={t("pageHelp.defaultTitle")}>
        <p dangerouslySetInnerHTML={{ __html: t("mgmtLeavePolicies.help") }} />
      </PageHelpBanner>
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtLeavePolicies.title")}</h2>
        <p className="text-sm text-gray-500">{t("mgmtLeavePolicies.subtitle")}</p>
      </div>

      {canManageLeave && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="create-policy-form">
          <Input data-testid="policy-key-input" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value.toUpperCase().replace(/\s/g, "_") })} placeholder={t("mgmtLeavePolicies.keyPlaceholder")} />
          <Input data-testid="policy-title-input" value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} placeholder={t("mgmtLeavePolicies.titleEnPlaceholder")} />
          <Input data-testid="policy-days-input" type="number" value={form.annual_days} onChange={(e) => setForm({ ...form, annual_days: e.target.value })} placeholder={t("mgmtLeavePolicies.daysPlaceholder")} />
          <Input data-testid="policy-effective-from-input" type="date" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} />
          <Input data-testid="policy-source-input" value={form.source_document} onChange={(e) => setForm({ ...form, source_document: e.target.value })} placeholder={t("mgmtLeavePolicies.sourcePlaceholder")} className="col-span-2" />
          <Button data-testid="create-policy-btn" onClick={createPolicy}>{t("mgmtLeavePolicies.createPolicy")}</Button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left"><tr><th className="px-4 py-3 font-medium">{t("mgmtLeavePolicies.colKey")}</th><th className="px-4 py-3 font-medium">{t("mgmtLeavePolicies.colTitle")}</th><th className="px-4 py-3 font-medium">{t("mgmtLeavePolicies.colDays")}</th><th className="px-4 py-3 font-medium">{t("mgmtLeavePolicies.colEffectiveFrom")}</th><th className="px-4 py-3 font-medium">{t("common.status")}</th><th className="px-4 py-3 font-medium"></th></tr></thead>
          <tbody>
            {policies.map((p) => (
              <tr key={p.id} className="border-t border-gray-100" data-testid={`policy-row-${p.id}`}>
                <td className="px-4 py-2 font-semibold text-gray-800">{p.key}</td>
                <td className="px-4 py-2 text-gray-600">{p.title_en}</td>
                <td className="px-4 py-2 text-gray-600">{p.annual_days}</td>
                <td className="px-4 py-2 text-gray-600">{p.effective_from}</td>
                <td className="px-4 py-2"><StatusBadge status={p.status} /></td>
                <td className="px-4 py-2">
                  {p.status === "DRAFT" && canManageLeave && <Button size="sm" data-testid={`policy-approve-btn-${p.id}`} onClick={() => approve(p.id)} className="bg-emerald-600 hover:bg-emerald-700">{t("mgmtLeavePolicies.approve")}</Button>}
                </td>
              </tr>
            ))}
            {policies.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500" data-testid="no-policies-row">{t("mgmtLeavePolicies.noPolicies")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

